import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';

export interface ChatMediaItem {
  id: string;
  message_id: string;
  url: string;
  type: 'image' | 'video';
  file_name?: string;
  date: string;
  sender_uid?: string;
  sender_name?: string;
}

export interface ChatFileItem {
  id: string;
  message_id: string;
  url: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  size_label?: string;
  date: string;
  sender_uid?: string;
  sender_name?: string;
}

export interface ChatLinkItem {
  id: string;
  message_id: string;
  url: string;
  domain: string;
  title?: string;
  description?: string;
  display_url?: string;
  date: string;
  sender_uid?: string;
  sender_name?: string;
}

export interface ChatMediaResponse {
  chatId: string;
  media: ChatMediaItem[];
  files: ChatFileItem[];
  links: ChatLinkItem[];
  summary: {
    totalMedia: number;
    totalFiles: number;
    totalLinks: number;
  };
}

export const fetchChatMedia = async (
  chatId: string,
  inquiryId?: string,
): Promise<ChatMediaResponse> => {
  const token = (await auth.currentUser?.getIdToken()) || sessionStore.getState().token;
  if (!token) {
    throw new Error('Authentication required.');
  }

  const queryParams = new URLSearchParams({ chatId });
  if (inquiryId) {
    queryParams.append('inquiryId', inquiryId);
  }

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_CHAT_MEDIA}?${queryParams.toString()}`;
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || 'Failed to fetch chat media.');
  }

  return response.json();
};

export const useGetChatMedia = (chatId: string, inquiryId?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['chat-media', chatId, inquiryId],
    queryFn: () => fetchChatMedia(chatId, inquiryId),
    enabled: Boolean(chatId) && enabled,
    staleTime: 30 * 1000,
  });
};
